import { BlobReader, ZipReader, configure } from "@zip.js/zip.js";
import type { AppVersionMetadata } from "../types/appVersionMetadata";

// Vite bundles in-browser, so run everything in the same thread to avoid worker asset plumbing.
configure({ useWebWorkers: false });

export type ZipTreeNode = {
  id: string;
  name: string;
  path: string;
  isDirectory: boolean;
  children: ZipTreeNode[];
};

export type ZipHeuristics = {
  suggestedOperatingSystem?: AppVersionMetadata["operatingSystem"];
  suggestedGameEngine?: AppVersionMetadata["gameEngine"];
  suggestedExecutable?: string;
  windowsScore: number;
  linuxScore: number;
  unityScore: number;
  unrealScore: number;
  executableCandidates: string[];
  hasPixelStreaming: boolean;
};

export type ZipAnalysis = {
  nodes: ZipTreeNode[];
  heuristics: ZipHeuristics;
};

type CandidateScore = {
  path: string;
  score: number;
};

const MAC_OS_TRASH = /^__macosx\//i;

/** Known non-game executable filenames that should never be selected as the primary exe. */
const EXCLUDED_EXECUTABLES = new Set([
  "crashreportclient.exe",
  "crashreportclient",
  "ue4prereqsetup_x64.exe",
  "ueprereqsetup_x64.exe",
  "epicwebhelper.exe",
  "unrealcefsubprocess.exe",
  "unitycrashandler64.exe",
  "unitycrashandler32.exe",
  "stunserver.exe",
  "turnserver.exe",
]);

/**
 * Path substrings that indicate an engine-internal helper location.
 * Anything under these paths is never the game executable in a packaged Unreal build —
 * the real game binary lives under `[ProjectName]/Binaries/...`, not `Engine/Binaries/...`.
 */
const EXCLUDED_PATH_PREFIXES = [
  "engine/binaries/",
];

export async function analyzeZipArchive(file: File): Promise<ZipAnalysis> {
  const zipReader = new ZipReader(new BlobReader(file));
  const root: ZipTreeNode = {
    id: "__root__",
    name: "",
    path: "",
    isDirectory: true,
    children: []
  };
  const nodeMap = new Map<string, ZipTreeNode>();
  nodeMap.set(root.id, root);

  const heuristics: ZipHeuristics = {
    windowsScore: 0,
    linuxScore: 0,
    unityScore: 0,
    unrealScore: 0,
    executableCandidates: [],
    hasPixelStreaming: false,
  };
  const scoredCandidates: CandidateScore[] = [];

  try {
    const entries = await zipReader.getEntries();

    for (const entry of entries) {
      const rawName = entry.filename || "";
      const isDir = Boolean(entry.directory);
      const normalizedPath = normalizeZipPath(rawName, isDir);
      if (!normalizedPath || MAC_OS_TRASH.test(normalizedPath)) {
        continue;
      }

      insertNode(root, nodeMap, normalizedPath, isDir);

      if (!isDir) {
        evaluateEntry(normalizedPath, heuristics, scoredCandidates);
      }
    }
  } finally {
    await zipReader.close();
  }

  sortNodes(root);

  const suggestedOperatingSystem = pickSuggestedOption(
    heuristics.windowsScore,
    heuristics.linuxScore,
    "windows",
    "linux"
  );
  const suggestedGameEngine = pickSuggestedOption(
    heuristics.unrealScore,
    heuristics.unityScore,
    "unreal",
    "unity"
  );

  const suggestedExecutable = pickExecutable(scoredCandidates);
  if (suggestedExecutable) {
    heuristics.suggestedExecutable = suggestedExecutable;
  }
  heuristics.suggestedOperatingSystem = suggestedOperatingSystem;
  heuristics.suggestedGameEngine = suggestedGameEngine;

  return {
    nodes: root.children,
    heuristics
  };
}

function normalizeZipPath(path: string, isDirectory: boolean): string {
  const normalized = path
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");
  const trimmed = isDirectory ? normalized.replace(/\/+$/, "") : normalized;
  return trimmed;
}

function insertNode(
  root: ZipTreeNode,
  nodeMap: Map<string, ZipTreeNode>,
  fullPath: string,
  isDir: boolean
) {
  const segments = fullPath.split("/").filter(Boolean);
  if (!segments.length) return;

  let parent = root;
  let currentPath = "";

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const isLast = i === segments.length - 1;
    currentPath = currentPath ? `${currentPath}/${segment}` : segment;
    let node = nodeMap.get(currentPath);

    if (!node) {
      node = {
        id: currentPath,
        name: segment,
        path: currentPath,
        isDirectory: isLast ? isDir : true,
        children: []
      };
      parent.children.push(node);
      nodeMap.set(currentPath, node);
    } else if (isLast && isDir) {
      node.isDirectory = true;
    }

    parent = node;
  }
}

function sortNodes(node: ZipTreeNode) {
  node.children.sort((a, b) => {
    if (a.isDirectory !== b.isDirectory) {
      return a.isDirectory ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
  node.children.forEach(sortNodes);
}

function evaluateEntry(
  path: string,
  heuristics: ZipHeuristics,
  scoredCandidates: CandidateScore[]
) {
  const lower = path.toLowerCase();
  const fileName = lower.split("/").pop() || lower;
  const excluded =
    EXCLUDED_EXECUTABLES.has(fileName) ||
    EXCLUDED_PATH_PREFIXES.some((prefix) => lower.includes(prefix));

  if (isWindowsExecutable(fileName)) {
    heuristics.windowsScore += 4;
    if (!excluded) heuristics.executableCandidates.push(path);
  }
  if (fileName.endsWith(".bat") || fileName.endsWith(".cmd")) {
    heuristics.windowsScore += 2;
  }
  if (isLinuxExecutable(fileName, lower)) {
    heuristics.linuxScore += 3;
    if (!excluded) heuristics.executableCandidates.push(path);
  }
  if (isUnityLinuxExecutable(fileName)) {
    heuristics.linuxScore += 3;
    heuristics.unityScore += 3;
    if (!excluded) heuristics.executableCandidates.push(path);
  }
  if (lower.includes("/bin/") || lower.includes("linux")) {
    heuristics.linuxScore += 1;
  }
  if (lower.includes("win64") || lower.includes("windows")) {
    heuristics.windowsScore += 1;
  }

  if (isUnityFile(lower)) {
    heuristics.unityScore += 3;
  }
  if (isUnrealFile(lower)) {
    heuristics.unrealScore += 3;
  }

  if (lower.includes("pixelstreaming/")) {
    heuristics.hasPixelStreaming = true;
  }

  if (!excluded) {
    const score = scoreExecutableCandidate(lower) - nestingDepth(lower);
    if (score > 0) {
      scoredCandidates.push({ path, score });
    }
  }
}

function isWindowsExecutable(fileName: string) {
  return fileName.endsWith(".exe");
}

function isLinuxExecutable(fileName: string, fullPath: string) {
  if (fileName.endsWith(".sh")) return true;
  if (!fileName.includes(".")) {
    if (fullPath.includes("/bin/") || fullPath.includes("/linux")) {
      return true;
    }
  }
  return false;
}

function isUnityLinuxExecutable(fileName: string) {
  return fileName.endsWith(".x86_64") || fileName.endsWith(".x86");
}

function isUnityFile(path: string) {
  return (
    path.includes("unityplayer") ||
    path.includes("unitycrashhandler") ||
    path.includes("_data/") ||
    path.endsWith(".unitypackage") ||
    path.endsWith(".x86_64") ||
    path.endsWith(".x86")
  );
}

function isUnrealFile(path: string) {
  return (
    path.endsWith(".uproject") ||
    path.includes("/binaries/win") ||
    path.includes("/binaries/linux") ||
    path.includes("/engine/binaries") ||
    path.includes("paks/") ||
    path.endsWith("server.target") ||
    path.includes("manifest_nonufsfiles_") ||
    path.includes("manifest_debugfiles_")
  );
}

function scoreExecutableCandidate(lowerPath: string) {
  let score = 0;
  if (
    lowerPath.endsWith(".exe") ||
    lowerPath.endsWith(".sh") ||
    lowerPath.endsWith(".x86_64") ||
    lowerPath.endsWith(".x86")
  ) {
    score += 3;
  }
  if (lowerPath.includes("server")) score += 3;
  if (lowerPath.includes("dedicated")) score += 2;
  if (lowerPath.includes("/bin/") || lowerPath.includes("/binaries/")) score += 2;
  if (lowerPath.includes("win64") || lowerPath.includes("linux")) score += 1;
  if (lowerPath.endsWith(".bat") || lowerPath.endsWith(".cmd")) score += 1;
  return score;
}

function pickSuggestedOption<T extends string>(
  aScore: number,
  bScore: number,
  a: T,
  b: T
): T | undefined {
  if (aScore === 0 && bScore === 0) return undefined;
  if (Math.abs(aScore - bScore) < 2) return undefined;
  return aScore > bScore ? a : b;
}

function nestingDepth(path: string) {
  return (path.match(/\//g) || []).length;
}

function pickExecutable(candidates: CandidateScore[]): string | undefined {
  if (!candidates.length) return undefined;
  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return nestingDepth(a.path) - nestingDepth(b.path); // shallower wins
  });
  return candidates[0]?.path;
}
